import React, { useEffect, useState } from "react";
import { api, apiError } from "../api/client";
import { useI18n, formatMoney, formatDate } from "../i18n";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, Card } from "../components/shared";
import {
  TrendingUp, TrendingDown, Wallet, Pill, Stethoscope, AlertTriangle,
  PiggyBank, Target, Sparkles, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { toast } from "sonner";

export default function Insights() {
  const { t } = useI18n();
  const { tenant } = useAuth();
  const cur = tenant?.currency || "XOF";
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/insights/owner", { params: { days } });
        setData(data);
      } catch (err) {
        toast.error(apiError(err, t("common.error")));
      } finally {
        setLoading(false);
      }
    })();
  }, [days, t]);

  return (
    <div>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-brand" /> Pilotage clinique
          </span>
        }
        actions={
          <div className="inline-flex gap-1 p-1 rounded-xl bg-subtle" data-testid="period-tabs">
            {[7, 30, 90].map((d) => (
              <button key={d} onClick={() => setDays(d)} data-testid={`period-${d}`}
                className={`h-10 px-4 rounded-lg text-sm font-semibold transition-colors ${days === d ? "bg-white shadow-sm text-brand" : "text-ink-muted hover:text-ink"}`}>
                {d}j
              </button>
            ))}
          </div>
        }
      />

      {loading || !data ? (
        <Card className="py-16 text-center text-ink-muted">{t("common.loading")}</Card>
      ) : (
        <>
          {/* Top KPIs */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="insights-kpis">
            <BigKpi
              testid="kpi-revenue"
              icon={TrendingUp}
              tone="brand"
              label={`Recettes ${days}j`}
              value={formatMoney(data.revenue.total, cur)}
              growth={data.revenue.growth_pct}
            />
            <BigKpi
              testid="kpi-stock-cost"
              icon={TrendingDown}
              tone="warning"
              label={`Coût stock ${days}j`}
              value={formatMoney(data.stock_cost.total, cur)}
            />
            <BigKpi
              testid="kpi-profit"
              icon={Wallet}
              tone="success"
              label={`Marge brute ${days}j`}
              value={formatMoney(data.profit.total, cur)}
              hint={data.revenue.total > 0 ? `${Math.round(data.profit.total / data.revenue.total * 100)}% des recettes` : ""}
            />
            <BigKpi
              testid="kpi-collection"
              icon={Target}
              tone={data.collection_rate.rate_pct >= 80 ? "success" : data.collection_rate.rate_pct >= 50 ? "warning" : "danger"}
              label="Taux d'encaissement"
              value={`${data.collection_rate.rate_pct}%`}
              hint={`Impayés: ${formatMoney(data.collection_rate.outstanding, cur)}`}
            />
          </div>

          {/* Revenue chart */}
          <Card className="p-5 mb-6" data-testid="revenue-chart">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-brand" />
              <h3 className="font-heading font-bold text-ink">Recettes par jour</h3>
            </div>
            <div className="h-64 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.revenue.by_day}>
                  <defs>
                    <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#086950" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#086950" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E7E3D8" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v?.slice(5)} />
                  <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                  <Tooltip
                    formatter={(v) => formatMoney(v, cur)}
                    labelFormatter={(l) => formatDate(l)}
                    contentStyle={{ borderRadius: 12, border: "1px solid #E7E3D8" }}
                  />
                  <Area type="monotone" dataKey="amount" stroke="#086950" strokeWidth={2} fill="url(#rev)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Top medications */}
            <Card className="p-5" data-testid="top-medications">
              <div className="flex items-center gap-2 mb-3">
                <Pill className="w-5 h-5 text-terracotta" />
                <h3 className="font-heading font-bold text-ink">Top 5 médicaments par CA</h3>
              </div>
              {data.top_medications.length === 0 ? (
                <div className="text-sm text-ink-muted">Aucune donnée sur la période</div>
              ) : (
                <ul className="space-y-2">
                  {data.top_medications.map((m, i) => (
                    <RankRow key={m.name} rank={i + 1}
                      label={m.name} hint={`${m.qty} unité(s)`}
                      value={formatMoney(m.revenue, cur)}
                      tone="terracotta" />
                  ))}
                </ul>
              )}
            </Card>

            {/* Top doctors */}
            <Card className="p-5" data-testid="top-doctors">
              <div className="flex items-center gap-2 mb-3">
                <Stethoscope className="w-5 h-5 text-brand" />
                <h3 className="font-heading font-bold text-ink">Top 5 praticiens par volume</h3>
              </div>
              {data.top_doctors.length === 0 ? (
                <div className="text-sm text-ink-muted">Aucune donnée sur la période</div>
              ) : (
                <ul className="space-y-2">
                  {data.top_doctors.map((d, i) => (
                    <RankRow key={d.doctor_id} rank={i + 1}
                      label={d.name}
                      hint={`${d.consultations} consultation(s) · ${formatMoney(d.revenue, cur)}`}
                      value={`${d.consultations}`}
                      tone="brand" />
                  ))}
                </ul>
              )}
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Collection rate */}
            <Card className="p-5" data-testid="collection-card">
              <div className="flex items-center gap-2 mb-3">
                <Target className="w-5 h-5 text-brand" />
                <h3 className="font-heading font-bold text-ink">Taux d'encaissement</h3>
              </div>
              <div className="space-y-2">
                <Row k="Facturé" v={formatMoney(data.collection_rate.invoiced, cur)} />
                <Row k="Encaissé" v={formatMoney(data.collection_rate.collected, cur)} tone="success" />
                <Row k="Impayé" v={formatMoney(data.collection_rate.outstanding, cur)} tone={data.collection_rate.outstanding > 0 ? "danger" : "success"} strong />
                <div className="h-3 rounded-full bg-subtle overflow-hidden">
                  <div className={`h-full transition-all ${data.collection_rate.rate_pct >= 80 ? "bg-[#2d8a56]" : data.collection_rate.rate_pct >= 50 ? "bg-[#e3a03b]" : "bg-[#d34044]"}`} style={{ width: `${Math.min(100, data.collection_rate.rate_pct)}%` }} />
                </div>
                <div className="text-right text-xs text-ink-muted">{data.collection_rate.rate_pct}%</div>
              </div>
            </Card>

            {/* Cash variance */}
            <Card className="p-5" data-testid="variance-card">
              <div className="flex items-center gap-2 mb-3">
                <PiggyBank className="w-5 h-5 text-brand" />
                <h3 className="font-heading font-bold text-ink">Variance caisse cumulée</h3>
              </div>
              <div className="stat-number text-3xl font-heading font-extrabold text-ink">
                {formatMoney(data.cash_variance.sum_abs, cur)}
              </div>
              <div className="text-xs text-ink-muted mt-1">Somme des écarts absolus sur la période</div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 rounded-lg bg-subtle">
                  <div className="text-xs text-ink-muted">Sessions clôturées</div>
                  <div className="stat-number font-bold text-ink">{data.cash_variance.sessions}</div>
                </div>
                <div className={`p-2 rounded-lg ${data.cash_variance.sessions_with_variance > 0 ? "bg-[#e3a03b]/10" : "bg-subtle"}`}>
                  <div className="text-xs text-ink-muted">Avec écart</div>
                  <div className={`stat-number font-bold ${data.cash_variance.sessions_with_variance > 0 ? "text-[#8a5a10]" : "text-ink"}`}>
                    {data.cash_variance.sessions_with_variance}
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Chronic out of stock */}
          <Card className="p-5" data-testid="chronic-out">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-[#b12e31]" />
              <h3 className="font-heading font-bold text-ink">Ruptures chroniques</h3>
            </div>
            <div className="text-xs text-ink-muted mb-3">Médicaments actuellement en rupture mais consommés sur la période — priorité de réassort</div>
            {data.chronic_out_of_stock.length === 0 ? (
              <div className="text-sm text-ink-muted">Aucune rupture détectée sur cette période 👌</div>
            ) : (
              <ul className="space-y-2">
                {data.chronic_out_of_stock.map((p) => (
                  <li key={p.product_id} className="flex items-center justify-between p-3 rounded-xl bg-[#d34044]/5 border border-[#d34044]/20">
                    <div className="font-semibold text-ink">{p.name}</div>
                    <div className="text-sm text-[#b12e31] font-semibold">{p.out_movements} sortie(s) · stock 0</div>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

function BigKpi({ icon: Icon, label, value, hint, growth, tone, testid }) {
  const tones = {
    brand: "bg-brand/10 text-brand",
    success: "bg-[#2d8a56]/10 text-[#2d8a56]",
    warning: "bg-[#e3a03b]/15 text-[#8a5a10]",
    danger: "bg-[#d34044]/10 text-[#b12e31]",
  };
  return (
    <Card className="p-5" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className={`p-2.5 rounded-xl ${tones[tone]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs text-ink-muted uppercase tracking-wider">{label}</div>
          <div className="stat-number text-2xl font-heading font-extrabold text-ink">{value}</div>
          {growth !== undefined && growth !== null && (
            <div className={`inline-flex items-center gap-1 text-xs font-semibold mt-1 ${growth > 0 ? "text-[#2d8a56]" : growth < 0 ? "text-[#b12e31]" : "text-ink-muted"}`}>
              {growth > 0 ? <ArrowUpRight className="w-3 h-3" /> : growth < 0 ? <ArrowDownRight className="w-3 h-3" /> : <Minus className="w-3 h-3" />}
              {growth > 0 ? "+" : ""}{growth}% vs période précédente
            </div>
          )}
          {hint && <div className="text-xs text-ink-muted mt-1">{hint}</div>}
        </div>
      </div>
    </Card>
  );
}

function RankRow({ rank, label, hint, value, tone = "brand" }) {
  const badgeCls = tone === "terracotta" ? "bg-terracotta/10 text-terracotta" : "bg-brand/10 text-brand";
  return (
    <li className="flex items-center gap-3 p-2 rounded-xl hover:bg-subtle/50">
      <div className={`w-8 h-8 rounded-lg ${badgeCls} flex items-center justify-center font-heading font-bold text-sm`}>#{rank}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-ink truncate">{label}</div>
        <div className="text-xs text-ink-muted">{hint}</div>
      </div>
      <div className="stat-number font-bold text-ink shrink-0">{value}</div>
    </li>
  );
}

function Row({ k, v, strong, tone }) {
  const toneCls = tone === "danger" ? "text-[#b12e31]" : tone === "success" ? "text-[#2d8a56]" : "text-ink";
  return (
    <div className="flex items-center justify-between">
      <span className={`text-sm ${strong ? "font-semibold text-ink" : "text-ink-muted"}`}>{k}</span>
      <span className={`stat-number ${strong ? "font-bold text-base" : "text-sm"} ${toneCls}`}>{v}</span>
    </div>
  );
}
